import React, { memo, useEffect, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import CloseIconButton from "fe-common/ui/icons/closeIconButton";
import DialogContent from "@mui/material/DialogContent";
import styled from "@mui/material/styles/styled";
import Button from "@mui/material/Button";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { useShareLinks } from "../../../saveCase/useShareLinks";

//region [[ Styles ]]
const ShareCaseDialogView = styled(Dialog)(() => ({
  textAlign: "center",
}));

const StyledDialogTitle = styled(DialogContent)({
  padding: "12px 24px 0 24px",
  fontSize: "1.25rem",
  fontWeight: "bold",
});

const StyledDialogContent = styled(DialogContent)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const ContentText = styled("div")(() => ({
  maxWidth: "366px",
  textAlign: "center",
  fontSize: "16px",
  lineHeight: "28px",
}));

const ContentLink = styled("div")(() => ({
  margin: "2px",
  maxWidth: "366px",
  backgroundColor: KahunColorMap.Background,
}));

const ActionButton = styled(Button)(() => ({
  margin: "14px 32px",
  fontSize: "16px",
  textTransform: "capitalize",
  backgroundColor: KahunColorMap.PrimaryDim,
}));
//endregion [[ Styles ]]

//region [[ Props ]]
export interface ShareCaseDialogProps {
  shareId: string;
  isOpen: boolean;
  onClose: () => void;
}
//endregion [[ Props ]]

//region [[ Functions ]]
const TITLE = "Share Case";
const FIRST_LINE_TEXT =
  " The case link was copied to your clipboard, you can now share it with your colleagues.";
const SECOND_LINE_TEXT = "The case was added to 'My Cases' under main menu.";
//endregion [[ Functions ]]

export const ShareCaseDialog = memo(
  ({ shareId, isOpen, onClose }: ShareCaseDialogProps) => {
    const linkRef = useRef(null);
    const { shareLink } = useShareLinks();
    const link = shareLink(shareId);

    const copyToClipboard = (ref) => {
      ref.current.select();
      document.execCommand("copy");
      ref.current.blur();
    };

    useEffect(() => {
      shareId && copyToClipboard(linkRef);
    }, [shareId]);

    return (
      <div>
        <textarea
          ref={linkRef}
          style={{ position: "absolute", top: "-100px" }}
          value={link}
          readOnly
        />
        <ShareCaseDialogView
          open={isOpen}
          onClose={onClose}
          PaperProps={{ style: { borderRadius: "10px" } }}
        >
          <StyledDialogTitle>
            {TITLE}
            <CloseIconButton onClick={onClose} />
          </StyledDialogTitle>
          <StyledDialogContent>
            <ContentText>{FIRST_LINE_TEXT}</ContentText>
            <ContentLink>{link}</ContentLink>
            <ContentText>{SECOND_LINE_TEXT}</ContentText>
            <ActionButton
              color="primary"
              variant="contained"
              fullWidth
              onClick={onClose}
            >
              Got it!
            </ActionButton>
          </StyledDialogContent>
        </ShareCaseDialogView>
      </div>
    );
  },
);
